import { InfluxSeriesMeasuresData } from '@mv-submodules/inplant-plantanalysis-fe-iblu/functions/sharedV2'
import { Workshift } from '@mv-submodules/inplant-plantanalysis-fe-iblu/types/workshift'
import { FetchPromise } from '@mv-submodules/mvlabs-components-fe/functions/hooks/useGenericFetchHook'
import { ChartDerivedMeasure, ChartMeasure, ChartWordMeasure } from '../../../_types'
import {
  getInfluxSeriesMeasuresData,
  MeasureToFetchForSeries,
} from '@mv-submodules/inplant-plantanalysis-fe-iblu/functions/influx/getChartInfluxData'
import { useLazyCoreFetch } from '@mv-submodules/inplant-core-fe/functions/hooks/useVidimFetch'
import { useEffect } from 'react'
import Row from '@mv-submodules/inplant-components-fe/ui/components/Grid/Row'
import React from 'react'
import { TFunction } from 'i18next'
import { Loader } from '@mv-submodules/inplant-plantanalysis-fe-iblu/functions/shared'

interface Props<MeasureSlugs extends string = string> {
  plant: string
  workShift: Workshift
  t: TFunction
  children: (measuresData: InfluxSeriesMeasuresData<MeasureSlugs>) => JSX.Element
  measures: Array<ChartMeasure<MeasureSlugs>>
  binaryWords: ChartWordMeasure[]
  derivedMeasures: Array<ChartDerivedMeasure<MeasureSlugs>>
  assetId: string
}

function AwaitChartData(props: Props) {
  const { plant, workShift, t, measures, binaryWords, derivedMeasures, assetId } = props
  const [fetchData, { data, loading, error }] = useLazyCoreFetch(_promise)

  useEffect(() => {
    const abortController = new AbortController()
    fetchData(
      { plant, workShift, measures, binaryWords, derivedMeasures, assetId },
      { includeLastValueFromPreviousShift: true, abortController }
    )
    return () => {
      abortController.abort()
    }
  }, [plant, workShift])

  const noData = data && data.length === 0

  if (error) {
    return <div className="alert alert-danger w-100 col-sm-6 mx-auto">{t('plantAnalysis.fetchErrors')}</div>
  }

  if (loading || (!data && !loading && !error)) {
    return (
      <Row>
        <Loader />
      </Row>
    )
  }

  if (noData) {
    return <div className="alert alert-warning w-100 col-sm-6 mx-auto">{t('plantAnalysis.noDataAvailable')}</div>
  }

  return props.children(data!)
}

interface PromiseParams {
  assetId: string
  plant: string
  workShift: Workshift
  measures: ChartMeasure[]
  binaryWords: ChartWordMeasure[]
  derivedMeasures: ChartDerivedMeasure[]
}

interface PromiseOptions {
  includeLastValueFromPreviousShift: boolean
  abortController: AbortController
}

const _promise: FetchPromise<InfluxSeriesMeasuresData, [PromiseParams, PromiseOptions]> = (
  { plant, workShift, measures, binaryWords, derivedMeasures, assetId },
  { includeLastValueFromPreviousShift, abortController }
) => async () => {
  const measuresToFetch = measures.map<MeasureToFetchForSeries>(m => ({
    assetId: m.assetId ?? assetId,
    measureId: m.id,
    measureSlug: m.slug,
    type: 'standard',
  }))

  const binaryWordsToFetch = binaryWords.map<MeasureToFetchForSeries>(w => ({
    assetId: w.assetId ?? assetId,
    wordId: w.id,
    type: 'word',
    wordSlug: w.type,
  }))

  
  const measuresData = await getInfluxSeriesMeasuresData(
    {
      measuresToFetch: [...measuresToFetch, ...binaryWordsToFetch],
      plant,
      date: workShift.start,
      workshift: workShift,
    },
    { includeLastValueFromPreviousShift, abortController }
  )
  
  return measuresData.map(_measuresDatum => {
    // Start with a copy of the original datum
    const result = { ..._measuresDatum }
    
    // Apply transforms to standard measures (only if transform exists)
    for (const measure of measures) {
      if (measure.valueTransform) {
        const value = result[measure.slug]
        if (value !== null) {
          result[measure.slug] = measure.valueTransform(value)
        }
      }
    }
    
    // Compute and apply transforms to derived measures
    for (const derivedMeasure of derivedMeasures) {
      const derivedValue = derivedMeasure.derivationFn(_measuresDatum)
        result[derivedMeasure.slug] = derivedMeasure.valueTransform && derivedValue !== null
        ? derivedMeasure.valueTransform(derivedValue)
        : derivedValue
    }
    
    return result
  })
}

export default AwaitChartData
